import { DifficultySettings, GameMetrics, DDAState } from './types';

const MIN_FLIP_BACK_DELAY = 500;
const MAX_FLIP_BACK_DELAY = 1500;
const MIN_PREVIEW_TIME = 0;
const MAX_PREVIEW_TIME = 4000;
const MIN_GRID_SIZE = { rows: 2, cols: 2 };  // Minimum 2x2 (4 cards)
const MAX_GRID_SIZE = { rows: 6, cols: 6 };  // Maximum 6x6 (36 cards)

export const adjustDifficulty = (
  currentSettings: DifficultySettings,
  metrics: GameMetrics,
  ddaState: DDAState
): { newSettings: DifficultySettings; newDDAState: Partial<DDAState> } => {
  const { accuracy, averageReactionTime, errors } = metrics;
  let newSettings = { ...currentSettings };
  let ddaUpdates: Partial<DDAState> = {};

  // Check for promotion conditions
  const promotionEligible = 
    accuracy >= 0.90 &&                    // 90%+ required (harder to advance)
    averageReactionTime <= 1200 && 
    !ddaState.lastRoundWasRelief;

  // Check for relief conditions
  const reliefNeeded = 
    accuracy < 0.65 ||                     // <65% triggers relief (easier)
    errors >= 5;                           // Fewer errors allowed (easier relief)

  if (promotionEligible) {
    // Apply promotion changes
    newSettings.flipBackDelay = Math.max(
      MIN_FLIP_BACK_DELAY,
      currentSettings.flipBackDelay - 100
    );
    newSettings.previewTime = Math.max(
      MIN_PREVIEW_TIME,
      currentSettings.previewTime - 500
    );

    // Increase grid size immediately on promotion (removed consecutive requirement)
    const totalCards = currentSettings.gridSize.rows * currentSettings.gridSize.cols;
    if (totalCards < MAX_GRID_SIZE.rows * MAX_GRID_SIZE.cols) {
      newSettings.gridSize = increaseGridSize(currentSettings.gridSize);
      console.log('Matching Grid DDA: Increasing grid size', {
        oldSize: currentSettings.gridSize,
        newSize: newSettings.gridSize,
        accuracy: Math.round(accuracy * 100) + '%'
      });
    } else if (currentSettings.imageSimilarityLevel < 5) {
      // Max grid reached, increase similarity
      newSettings.imageSimilarityLevel++;
      console.log('Matching Grid DDA: Increasing similarity level', {
        newLevel: newSettings.imageSimilarityLevel
      });
    }

    ddaUpdates = {
      consecutivePromotions: ddaState.consecutivePromotions + 1,
      consecutiveReliefs: 0,
      lastRoundWasRelief: false,
      roundsSinceLastPromotion: 0,
    };
  } else if (reliefNeeded) {
    // Apply relief changes
    newSettings.flipBackDelay = Math.min(
      MAX_FLIP_BACK_DELAY,
      currentSettings.flipBackDelay + 150
    );

    if (ddaState.consecutiveReliefs > 0) {
      newSettings.gridSize = decreaseGridSize(currentSettings.gridSize);
      newSettings.previewTime = Math.min(
        MAX_PREVIEW_TIME,
        currentSettings.previewTime + 500
      );
      console.log('Matching Grid DDA: Decreasing difficulty', {
        oldSize: currentSettings.gridSize,
        newSize: newSettings.gridSize,
        accuracy: Math.round(accuracy * 100) + '%'
      });
    }

    // Enable hints if needed
    if (ddaState.reliefsInLastFiveRounds >= 2) {
      newSettings.hintsEnabled = true;
    }

    ddaUpdates = {
      consecutivePromotions: 0,
      consecutiveReliefs: ddaState.consecutiveReliefs + 1,
      reliefsInLastFiveRounds: ddaState.reliefsInLastFiveRounds + 1,
      lastRoundWasRelief: true,
      roundsSinceLastPromotion: ddaState.roundsSinceLastPromotion + 1,
    };
  } else {
    // No changes needed
    console.log('Matching Grid DDA: Maintaining difficulty', {
      accuracy: Math.round(accuracy * 100) + '%',
      gridSize: currentSettings.gridSize
    });
    ddaUpdates = {
      consecutivePromotions: 0,
      consecutiveReliefs: 0,
      lastRoundWasRelief: false,
      roundsSinceLastPromotion: ddaState.roundsSinceLastPromotion + 1,
    };
  }

  return { newSettings, newDDAState: ddaUpdates };
};

const increaseGridSize = (
  currentSize: { rows: number; cols: number }
): { rows: number; cols: number } => {
  // Progressive increase with varied grid sizes (including odd numbers):
  // 2x2 (4) -> 2x3 (6) -> 3x3 (9) -> 3x4 (12) -> 4x4 (16) -> 4x5 (20) -> 5x5 (25) -> 5x6 (30) -> 6x6 (36)
  const currentTotal = currentSize.rows * currentSize.cols;
  
  // Specific progression path for better gameplay variety
  if (currentSize.rows === 2 && currentSize.cols === 2) return { rows: 2, cols: 3 };
  if (currentSize.rows === 2 && currentSize.cols === 3) return { rows: 3, cols: 3 };
  if (currentSize.rows === 3 && currentSize.cols === 3) return { rows: 3, cols: 4 };
  if (currentSize.rows === 3 && currentSize.cols === 4) return { rows: 4, cols: 4 };
  if (currentSize.rows === 4 && currentSize.cols === 4) return { rows: 4, cols: 5 };
  if (currentSize.rows === 4 && currentSize.cols === 5) return { rows: 5, cols: 5 };
  if (currentSize.rows === 5 && currentSize.cols === 5) return { rows: 5, cols: 6 };
  if (currentSize.rows === 5 && currentSize.cols === 6) return { rows: 6, cols: 6 };
  
  // Fallback: prefer increasing columns first
  if (currentSize.cols < 6 && currentSize.cols < currentSize.rows + 2) {
    return { ...currentSize, cols: currentSize.cols + 1 };
  } else if (currentSize.rows < 6) {
    return { rows: currentSize.rows + 1, cols: currentSize.cols };
  }
  return currentSize;
};

const decreaseGridSize = (
  currentSize: { rows: number; cols: number }
): { rows: number; cols: number } => {
  // Progressive decrease - reverse of increase progression
  if (currentSize.rows === 6 && currentSize.cols === 6) return { rows: 5, cols: 6 };
  if (currentSize.rows === 5 && currentSize.cols === 6) return { rows: 5, cols: 5 };
  if (currentSize.rows === 5 && currentSize.cols === 5) return { rows: 4, cols: 5 };
  if (currentSize.rows === 4 && currentSize.cols === 5) return { rows: 4, cols: 4 };
  if (currentSize.rows === 4 && currentSize.cols === 4) return { rows: 3, cols: 4 };
  if (currentSize.rows === 3 && currentSize.cols === 4) return { rows: 3, cols: 3 };
  if (currentSize.rows === 3 && currentSize.cols === 3) return { rows: 2, cols: 3 };
  if (currentSize.rows === 2 && currentSize.cols === 3) return { rows: 2, cols: 2 };
  
  // Fallback: general decrease logic
  if (currentSize.rows > MIN_GRID_SIZE.rows && currentSize.rows >= currentSize.cols) {
    return { rows: currentSize.rows - 1, cols: currentSize.cols };
  } else if (currentSize.cols > MIN_GRID_SIZE.cols) {
    return { ...currentSize, cols: currentSize.cols - 1 };
  }
  return currentSize;
};